import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PortalEntryRunStatus,
  type PortalRunDto,
  type PortalRunLatestResponse,
  type PortalRunStartResponse,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import { apiJson, type ApiErrorBody } from './apiFetch';
import { reconcileReportDetailVersion } from './reportDetailOptimistic';

const POLL_WHILE_ACTIVE_MS = 2_000;

export const isPortalRunActive = (run: PortalRunDto | null | undefined): boolean => {
  const finished: readonly PortalEntryRunStatus[] = [PortalEntryRunStatus.Completed, PortalEntryRunStatus.Failed, PortalEntryRunStatus.Cancelled];
  return !!run && !finished.includes(run.status);
};

const apiError = (fallback: string) => (body: ApiErrorBody) => new Error(body.error || fallback);

/**
 * The report's latest portal-entry run, or with `noticeOf` its latest run of that row's 72/24-hour
 * notice, polled while it is still going. Opening it clears the report's badge.
 */
export function usePortalRun(reportId: string, enabled: boolean, noticeOf: string | null = null) {
  const { selectedCampaign } = useCampaignStore();
  const query = noticeOf ? `?noticeOf=${encodeURIComponent(noticeOf)}` : '';
  return useQuery({
    queryKey: queryKeys.finance.reports.portalRun(reportId, noticeOf),
    queryFn: () =>
      apiJson<PortalRunLatestResponse>(`/api/finance/reports/${reportId}/portal-run${query}`, {
        error: 'Failed to load the portal run',
      }),
    select: (response) => response.run,
    enabled: enabled && authService.isAuthenticated() && !!selectedCampaign,
    refetchInterval: (q) => (isPortalRunActive(q.state.data?.run) ? POLL_WHILE_ACTIVE_MS : false),
  });
}

interface StartPortalRun {
  reportId: string;
  concurrencyVersion: number;
  /** Runs the notice this row owes instead of the whole report. */
  noticeOf?: string | null;
}

/** Starts a run; the report's bumped concurrency token is written back into its cached detail. */
export function useStartPortalRun() {
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: ({ reportId, concurrencyVersion, noticeOf }: StartPortalRun) =>
      apiJson<PortalRunStartResponse>(`/api/finance/reports/${reportId}/portal-runs`, {
        method: 'POST',
        json: noticeOf ? { concurrencyVersion, noticeOf } : { concurrencyVersion },
        error: apiError('Failed to start the portal run'),
      }),
    onSuccess: ({ run, reportConcurrencyVersion }, { reportId, noticeOf }) => {
      reconcileReportDetailVersion(queryClient, reportId, reportConcurrencyVersion);
      queryClient.setQueryData<PortalRunLatestResponse>(queryKeys.finance.reports.portalRun(reportId, noticeOf), { run });
    },
    onSettled: (_data, _error, { reportId, noticeOf }) =>
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.portalRun(reportId, noticeOf) }),
  });
  return { startRun: mutate, isStarting: isPending, startError: error };
}

/** Cancels a run with its concurrency token; the cancelled run, with its new token, replaces the cached one. */
export function useCancelPortalRun() {
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: (run: PortalRunDto) =>
      apiJson<PortalRunDto>(`/api/finance/portal-runs/${run.id}/cancel`, {
        method: 'POST',
        json: { concurrencyVersion: run.concurrencyVersion },
        error: apiError('Failed to cancel the portal run'),
      }),
    onSuccess: (run) =>
      queryClient.setQueryData<PortalRunLatestResponse>(queryKeys.finance.reports.portalRun(run.reportId, run.noticeOf), { run }),
    onSettled: (_data, _error, run) =>
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.portalRun(run.reportId, run.noticeOf) }),
  });
  return { cancelRun: mutate, isCancelling: isPending, cancelError: error };
}
